/**
 * bizPhoneInput  —  International phone field component
 * ======================================================
 * Country data and flag emoji are provided by bizFlagUtils factory.
 *
 * Usage:
 *   <biz-phone-input ng-model="formData.mobilePhone"
 *                    biz-phone-valid="phoneValid"
 *                    placeholder="Mobile number"
 *                    default-iso2="detectedIso2">
 *   </biz-phone-input>
 *
 * Outputs:
 *   ng-model        → E.164 string "+14155552671"  or  null
 *   biz-phone-valid → null (empty) | true (valid) | false (invalid)
 */
(function () {
  'use strict';

  angular.module('bizmetryApp').component('bizPhoneInput', {

    bindings: {
      bizPhoneValid: '=',   // two-way: null | true | false
      placeholder:   '@',
      defaultIso2:   '@'    // ISO2 pre-select from parent (e.g. geo-detected country)
    },

    require: {
      ngModelCtrl: 'ngModel'
    },

    template: `
      <div class="biz-phone-field">

        <!-- Label row -->
        <div class="biz-phone-label-row">
          <span class="biz-phone-label">Mobile Phone</span>
          <span class="biz-phone-badge">
            <i class="material-icons biz-phone-badge-icon">lock</i>
            Optional &middot; 2FA trusted device
          </span>
        </div>

        <!-- Input row -->
        <div class="biz-phone-input-row"
             ng-class="{'focused': $ctrl.focused, 'has-error': $ctrl.localNumber && $ctrl.bizPhoneValid === false}">

          <!-- Country trigger -->
          <button type="button"
                  class="biz-phone-country-btn"
                  ng-click="$ctrl.toggleDropdown($event)"
                  aria-label="Select country ({{ $ctrl.selected.name }}, {{ $ctrl.selected.dial }})">
            <span class="biz-phone-flag">{{ $ctrl.selected.flag }}</span>
            <span class="biz-phone-dialcode">{{ $ctrl.selected.dial }}</span>
            <i class="material-icons biz-phone-caret" ng-class="{'open': $ctrl.dropdownOpen}">expand_more</i>
          </button>

          <div class="biz-phone-divider"></div>

          <!-- Number input -->
          <input class="biz-phone-number"
                 type="tel"
                 ng-model="$ctrl.localNumber"
                 ng-change="$ctrl.onNumberChange()"
                 ng-focus="$ctrl.focused = true"
                 ng-blur="$ctrl.focused = false"
                 placeholder="{{ $ctrl.placeholder || 'Phone number' }}" />
        </div>

        <!-- Dropdown -->
        <div class="biz-phone-dropdown"
             ng-show="$ctrl.dropdownOpen"
             ng-click="$event.stopPropagation()">

          <div class="biz-phone-search-wrap">
            <i class="material-icons biz-phone-search-icon">search</i>
            <input class="biz-phone-search"
                   type="text"
                   ng-model="$ctrl.query"
                   ng-change="$ctrl.onSearch()"
                   placeholder="Search country or code…" />
          </div>

          <!-- No query: preferred + all -->
          <div class="biz-phone-list" ng-if="!$ctrl.query">
            <div ng-repeat="c in $ctrl.preferred"
                 class="biz-phone-option"
                 ng-class="{'active': c.iso2 === $ctrl.selected.iso2}"
                 ng-click="$ctrl.pick(c)">
              <span class="biz-phone-opt-flag">{{ c.flag }}</span>
              <span class="biz-phone-opt-name">{{ c.name }}</span>
              <span class="biz-phone-opt-dial">{{ c.dial }}</span>
            </div>
            <div class="biz-phone-list-divider"></div>
            <div ng-repeat="c in $ctrl.allSorted"
                 class="biz-phone-option"
                 ng-class="{'active': c.iso2 === $ctrl.selected.iso2}"
                 ng-click="$ctrl.pick(c)">
              <span class="biz-phone-opt-flag">{{ c.flag }}</span>
              <span class="biz-phone-opt-name">{{ c.name }}</span>
              <span class="biz-phone-opt-dial">{{ c.dial }}</span>
            </div>
          </div>

          <!-- With query: filtered results -->
          <div class="biz-phone-list" ng-if="$ctrl.query">
            <div ng-repeat="c in $ctrl.results"
                 class="biz-phone-option"
                 ng-class="{'active': c.iso2 === $ctrl.selected.iso2}"
                 ng-click="$ctrl.pick(c)">
              <span class="biz-phone-opt-flag">{{ c.flag }}</span>
              <span class="biz-phone-opt-name">{{ c.name }}</span>
              <span class="biz-phone-opt-dial">{{ c.dial }}</span>
            </div>
            <div ng-if="$ctrl.results.length === 0" class="biz-phone-no-results">
              <i class="material-icons">search_off</i> No countries found
            </div>
          </div>
        </div>

        <!-- Status row -->
        <div class="biz-phone-status">
          <div class="biz-phone-hint" ng-if="!$ctrl.localNumber">
            <i class="material-icons">shield</i>
            Recommended for account security &amp; recovery
          </div>
          <div class="biz-phone-valid" ng-if="$ctrl.localNumber && $ctrl.bizPhoneValid === true">
            <i class="material-icons">check_circle</i>
            Valid number
          </div>
          <div class="biz-phone-invalid" ng-if="$ctrl.localNumber && $ctrl.bizPhoneValid === false">
            <i class="material-icons">error_outline</i>
            {{ $ctrl.getPhoneHint() }}
          </div>
        </div>

      </div>
    `,

    controller: ['$document', '$timeout', 'bizFlagUtils', function ($document, $timeout, bizFlagUtils) {
      var $ctrl = this;

      // ── Data from shared factory ───────────────────────────────────────
      $ctrl.preferred  = bizFlagUtils.PREFERRED_LIST;
      $ctrl.allSorted  = bizFlagUtils.SORTED_ALL;
      $ctrl.results    = [];
      $ctrl.query      = '';
      $ctrl.selected   = bizFlagUtils.ALL_COUNTRIES.find(function (c) { return c.iso2 === 'US'; });
      $ctrl.localNumber  = '';
      $ctrl.dropdownOpen = false;
      $ctrl.focused      = false;

      // ── Lifecycle ──────────────────────────────────────────────────────
      $ctrl.$onInit = function () {
        // Wire ngModel → component
        $ctrl.ngModelCtrl.$render = function () {
          var val = $ctrl.ngModelCtrl.$viewValue;
          if (!val) { $ctrl.localNumber = ''; return; }
          var matched = bizFlagUtils.SORTED_ALL.find(function (c) { return val.startsWith(c.dial); });
          if (matched) {
            $ctrl.selected    = matched;
            $ctrl.localNumber = val.slice(matched.dial.length);
          } else {
            $ctrl.localNumber = val;
          }
        };
      };

      // React when parent sets defaultIso2 (e.g. after geo-detection resolves)
      $ctrl.$onChanges = function (changes) {
        if (changes.defaultIso2 && changes.defaultIso2.currentValue && !$ctrl.localNumber) {
          var iso2    = changes.defaultIso2.currentValue.toUpperCase();
          var country = bizFlagUtils.ALL_COUNTRIES.find(function (c) { return c.iso2 === iso2; });
          if (country) $ctrl.selected = country;
        }
      };

      $ctrl.$onDestroy = function () {
        $document.off('click', onDocClick);
      };

      // ── Dropdown ───────────────────────────────────────────────────────
      $ctrl.toggleDropdown = function ($event) {
        $event.stopPropagation();
        $ctrl.dropdownOpen = !$ctrl.dropdownOpen;
        if ($ctrl.dropdownOpen) {
          $ctrl.query   = '';
          $ctrl.results = [];
          $timeout(function () {
            var search = document.querySelector('.biz-phone-search');
            if (search) search.focus();
          }, 50);
        }
      };

      $ctrl.pick = function (country) {
        $ctrl.selected     = country;
        $ctrl.dropdownOpen = false;
        $ctrl.query        = '';
        $ctrl.onNumberChange();
        $timeout(function () {
          var input = document.querySelector('.biz-phone-number');
          if (input) input.focus();
        }, 30);
      };

      $ctrl.onSearch = function () {
        var raw = ($ctrl.query || '').trim();
        if (!raw) { $ctrl.results = []; return; }

        var q      = raw.toLowerCase();
        var digits = raw.replace(/[^\d]/g, '');
        var isDial = digits.length > 0 && /^[+\d]/.test(raw);

        var scored = [];

        bizFlagUtils.SORTED_ALL.forEach(function (c) {
          var score   = 0;
          var nameLow = c.name.toLowerCase();

          if (isDial) {
            var needle = '+' + digits;
            if (c.dial === needle)              score = 100;
            else if (c.dial.startsWith(needle)) score = 50;
          }

          if (c.iso2.toLowerCase() === q)       score = Math.max(score, 80);
          if (nameLow.startsWith(q))            score = Math.max(score, 70);
          else if (nameLow.indexOf(q) !== -1)   score = Math.max(score, 30);

          if (score > 0) scored.push({ c: c, score: score });
        });

        scored.sort(function (a, b) {
          return b.score !== a.score
            ? b.score - a.score
            : a.c.dial.length - b.c.dial.length;
        });

        $ctrl.results = scored.map(function (s) { return s.c; });
      };

      function onDocClick() {
        if ($ctrl.dropdownOpen) {
          $ctrl.dropdownOpen = false;
          try { $ctrl.$apply && $ctrl.$apply(); } catch (e) {}
        }
      }
      $document.on('click', onDocClick);

      // ── Validation ─────────────────────────────────────────────────────
      $ctrl.getPhoneHint = function () {
        var range  = bizFlagUtils.getExpectedLength($ctrl.selected.iso2);
        var digits = ($ctrl.localNumber || '').replace(/\D/g, '');
        if (range[0] === range[1]) {
          return $ctrl.selected.name + ' requires exactly ' + range[0] + ' digits (' + digits.length + '/' + range[0] + ')';
        }
        return $ctrl.selected.name + ' requires ' + range[0] + '–' + range[1] + ' digits (' + digits.length + ' entered)';
      };

      $ctrl.onNumberChange = function () {
        var raw = $ctrl.localNumber ? $ctrl.localNumber.trim() : '';

        if (!raw) {
          $ctrl.ngModelCtrl.$setViewValue(null);
          $ctrl.bizPhoneValid = null;
          return;
        }

        var e164    = $ctrl.selected.dial + raw.replace(/\s/g, '');
        var digits  = raw.replace(/\D/g, '');
        var range   = bizFlagUtils.getExpectedLength($ctrl.selected.iso2);
        var isValid = digits.length >= range[0] && digits.length <= range[1];

        $ctrl.ngModelCtrl.$setViewValue(e164);
        $ctrl.bizPhoneValid = isValid;
      };

    }]

  });

}());